import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getTransform } from "../../../../utils/getTransform";
import { TOOL_PREP_PREVIEW_BG } from "../../../../utils/editorEntrySession";
import {
  computeFittedStageSize,
  TOOL_PREP_STAGE_INSET,
} from "../../lib/toolPrepImageLayout";
import RoundLoader from "../../../../components/ui/RoundLoader";
import {
  getWatermarkFontStack,
  measureWatermarkText,
  splitWatermarkScriptText,
  watermarkEffectStyle,
  watermarkFontSizePx,
  watermarkFontCssStyle,
  watermarkTextDecoration,
  watermarkTextAdvancedCss,
  watermarkTextBoxLayout,
  watermarkLogoDisplaySize,
  watermarkTilePositions,
  watermarkRotationPreservingCenter,
  resolveSingleMarkPosition,
  syncMarginFromNearestEdge,
  WATERMARK_CONTENT_TYPES,
} from "./watermarkSettings";
import {
  watermarkLogoMarkStyle,
  resolveWatermarkCropHostFrames,
} from "../Editor/watermarkImageFormatAdapter";
import WatermarkLogoCropOverlay from "./WatermarkLogoCropOverlay";
import WatermarkLogoMarkMedia from "./WatermarkLogoMarkMedia";
import WatermarkThumbMarks from "./WatermarkThumbMarks";
import {
  DEFAULT_CROP_RECT,
  DEFAULT_IMAGE_FRAME,
  hostFramesToSourceRect,
  normalizeCropRect,
} from "../../../../components/shared/imageToolbar/imageCropModel";
import ToolPrepCanvasStage from "../../shared/Popup/ToolPrepCanvasStage";
import "../../object/shared/Popup/TransformPreview.css";
import "./WatermarkPreview.css";

const HANDLES = ["nw", "ne", "sw", "se", "w", "e"];

function markFitsOnCanvas(mark, insetX = 0, insetY = 0) {
  if (!mark) return false;
  const x0 = Math.min(0.45, Math.max(0, insetX));
  const y0 = Math.min(0.45, Math.max(0, insetY));
  return (
    mark.x >= x0 - 0.001 &&
    mark.x <= 1 - x0 + 0.001 &&
    mark.y >= y0 - 0.001 &&
    mark.y <= 1 - y0 + 0.001
  );
}

/** Tile grids include extra marks past the stage. Keep the selection on one inside it. */
function pickTileSelectionIndex(marks, anchor, inset) {
  if (!marks?.length) return null;
  const insetX = inset?.x || 0;
  const insetY = inset?.y || 0;
  const fitting = [];
  const inside = [];
  for (let i = 0; i < marks.length; i += 1) {
    if (markFitsOnCanvas(marks[i], insetX, insetY)) fitting.push(i);
    else if (markFitsOnCanvas(marks[i], 0, 0)) inside.push(i);
  }
  const pool = fitting.length
    ? fitting
    : inside.length
      ? inside
      : marks.map((_, i) => i);
  const target = anchor || { x: 0.5, y: 0.5 };
  let best = pool[0];
  let bestDist = Infinity;
  pool.forEach((i) => {
    const m = marks[i];
    const d = (m.x - target.x) ** 2 + (m.y - target.y) ** 2;
    if (d < bestDist) {
      bestDist = d;
      best = i;
    }
  });
  return best;
}

function WatermarkMarkText({ text, script = "normal" }) {
  const display = String(text || "").trim() ? text : "Your Text";
  if (script !== "super" && script !== "sub") {
    return <span className="watermark-preview__mark-text">{display}</span>;
  }
  const { base, affix } = splitWatermarkScriptText(display);
  if (!affix) {
    return <span className="watermark-preview__mark-text">{display}</span>;
  }
  return (
    <span className={`watermark-preview__mark-text watermark-preview__mark-text--${script}`}>
      <span className="watermark-preview__mark-base">{base}</span>
      <span className={`watermark-preview__mark-affix watermark-preview__mark-affix--${script}`}>
        {affix}
      </span>
    </span>
  );
}

/**
 * Image stage + live watermark overlay with selection (move / resize).
 */
const WatermarkPreview = ({
  imageUrl = null,
  loading = false,
  loadingLabel = "Loading preview…",
  emptyLabel = "Upload an image to preview",
  previewBg = TOOL_PREP_PREVIEW_BG.TRANSPARENT,
  watermark = null,
  /** Optional second mark drawn read-only (e.g. Sign PDF initials while editing signature). */
  ghostWatermark = null,
  /** Additional read-only marks (Sign PDF multi-placement). */
  extraWatermarks = null,
  /** Called when a selectable extra mark is clicked (Sign PDF). */
  onSelectExtraWatermark = null,
  onWatermarkChange = null,
  /** original = hide marks; preview = show marks (driven by left panel) */
  compareMode = "preview",
  /** 0–1 fit inside the stage container; default matches image tool prep. */
  stageInset = TOOL_PREP_STAGE_INSET,
  /** Optional HTML5 drop of field kinds (e.g. Sign PDF palette). */
  onFieldDrop = null,
}) => {
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [stageSize, setStageSize] = useState({ width: 420, height: 420 });
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const stageRef = React.useRef(null);
  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const marksRef = useRef([]);
  const selectionAnchorRef = useRef(null);
  const selectedIndexRef = useRef(null);
  const editInputRef = useRef(null);
  const editDraftRef = useRef("");
  const editingIndexRef = useRef(null);
  const editFocusTokenRef = useRef(0);
  const editReadyRef = useRef(false);
  const lastTapRef = useRef({ time: 0, index: null });
  const [editDraft, setEditDraft] = useState("");
  const textEditing = editingIndex != null;
  const watermarkRef = useRef(watermark);
  watermarkRef.current = watermark;
  const showMarks = compareMode !== "original";
  const showMarksRef = useRef(showMarks);
  showMarksRef.current = showMarks;

  const patchWatermark = useCallback(
    (partial) => {
      if (!onWatermarkChange) return;
      onWatermarkChange(partial);
    },
    [onWatermarkChange]
  );

  const patchWatermarkRef = useRef(patchWatermark);
  patchWatermarkRef.current = patchWatermark;

  useEffect(() => {
    if (!imageUrl) {
      setNaturalSize({ width: 0, height: 0 });
      return undefined;
    }
    let cancelled = false;
    const img = new Image();
    if (
      typeof imageUrl === "string" &&
      /^https?:\/\//i.test(imageUrl) &&
      !imageUrl.startsWith("blob:") &&
      !imageUrl.startsWith("data:")
    ) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => {
      if (cancelled) return;
      setNaturalSize({
        width: img.naturalWidth || img.width,
        height: img.naturalHeight || img.height,
      });
    };
    img.onerror = () => {
      if (!cancelled) setNaturalSize({ width: 0, height: 0 });
    };
    img.src = imageUrl;
    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const update = () => {
      const aspect =
        naturalSize.width > 0 && naturalSize.height > 0
          ? naturalSize.width / naturalSize.height
          : 1;
      const fitted = computeFittedStageSize(
        el.clientWidth,
        el.clientHeight,
        aspect,
        { inset: stageInset }
      );
      if (!fitted) return;
      setStageSize(fitted);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [naturalSize, stageInset]);

  const transform = useMemo(() => {
    if (!naturalSize.width || !naturalSize.height) {
      return { width: 0, height: 0, x: 0, y: 0 };
    }
    return getTransform(
      naturalSize.width,
      naturalSize.height,
      stageSize.width,
      stageSize.height,
      "fit"
    );
  }, [naturalSize, stageSize]);

  let canvasBg = "#1a1a1a";
  if (previewBg === TOOL_PREP_PREVIEW_BG.WHITE) {
    canvasBg = "#FFFFFF";
  } else if (previewBg === TOOL_PREP_PREVIEW_BG.TRANSPARENT) {
    canvasBg = "transparent";
  }

  const checker =
    previewBg === TOOL_PREP_PREVIEW_BG.TRANSPARENT ||
    !imageUrl ||
    (previewBg === TOOL_PREP_PREVIEW_BG.ORIGINAL && !imageUrl);

  const fontFamily = getWatermarkFontStack(watermark?.fontId);
  const rotation = Number(watermark?.rotation) || 0;
  const offsetX = Number(watermark?.offsetX) || 0;
  const offsetY = Number(watermark?.offsetY) || 0;
  const isLogoMode =
    watermark?.contentType === WATERMARK_CONTENT_TYPES.LOGO;
  const logoUrl = isLogoMode ? watermark?.logoUrl || null : null;
  const hasLogo = Boolean(logoUrl);

  const fontSizePx = watermarkFontSizePx(
    watermark?.size,
    stageSize.width,
    stageSize.height
  );

  const logoCropActive = Boolean(watermark?.logoCropActive);
  const logoCropRect = watermark?.logoCropRect
    ? normalizeCropRect(watermark.logoCropRect)
    : null;
  const cropHostFrames = useMemo(() => {
    if (!logoCropActive) return null;
    return resolveWatermarkCropHostFrames(watermark);
  }, [logoCropActive, watermark]);

  const logoFullSize = useMemo(
    () =>
      watermarkLogoDisplaySize(
        watermark?.size,
        stageSize.width,
        stageSize.height,
        watermark?.logoNaturalWidth,
        watermark?.logoNaturalHeight,
        null
      ),
    [
      watermark?.size,
      watermark?.logoNaturalWidth,
      watermark?.logoNaturalHeight,
      stageSize.width,
      stageSize.height,
    ]
  );

  const logoSize = useMemo(
    () =>
      logoCropActive
        ? logoFullSize
        : watermarkLogoDisplaySize(
            watermark?.size,
            stageSize.width,
            stageSize.height,
            watermark?.logoNaturalWidth,
            watermark?.logoNaturalHeight,
            logoCropRect
          ),
    [
      logoCropActive,
      logoFullSize,
      watermark?.size,
      watermark?.logoNaturalWidth,
      watermark?.logoNaturalHeight,
      logoCropRect,
      stageSize.width,
      stageSize.height,
    ]
  );

  const textMetrics = useMemo(
    () =>
      measureWatermarkText(watermark?.text || "Your Text", fontSizePx, fontFamily, watermark?.fontWeight
      , {
        letterSpacing: watermark?.letterSpacing,
        textScript: watermark?.textScript,
      }),
    [
      watermark?.text,
      fontSizePx,
      fontFamily,
      watermark?.fontWeight,
      watermark?.letterSpacing,
      watermark?.textScript,
    ]
  );

  const markMetrics = isLogoMode
    ? { width: logoSize.width, height: logoSize.height }
    : textMetrics;
  const markMetricsRef = useRef(markMetrics);
  markMetricsRef.current = markMetrics;

  const hasContent = isLogoMode
    ? hasLogo
    : Boolean(watermark?.text?.trim());

  const marks = useMemo(() => {
    if (!hasContent) return [];
    return watermarkTilePositions({
      tile: watermark.tile || "single",
      spacing: watermark.spacing,
      stageWidth: stageSize.width,
      stageHeight: stageSize.height,
      textWidth: markMetrics.width,
      textHeight: markMetrics.height,
      fontSizePx: isLogoMode
        ? Math.max(markMetrics.width, markMetrics.height)
        : fontSizePx,
      rotationDeg: rotation,
      position: watermark.position || "center",
      margin: watermark.margin,
      offsetX,
      offsetY,
      flushEdges: watermark.flushEdges !== false,
    });
  }, [
    hasContent,
    watermark,
    stageSize,
    markMetrics,
    fontSizePx,
    rotation,
    isLogoMode,
    offsetX,
    offsetY,
  ]);
  marksRef.current = marks;

  // Tile index is not a stable mark — the grid rebuilds when size or pan changes.
  // Follow the mark the user grabbed, and never park the box on an off-canvas tile.
  useEffect(() => {
    if (!marks.length) {
      setSelectedIndex(null);
      return;
    }
    const tile = watermark?.tile || "single";
    if (tile === "single") {
      setSelectedIndex((prev) =>
        prev != null && prev < marks.length ? prev : 0
      );
      return;
    }
    const metrics = markMetricsRef.current || { width: 0, height: 0 };
    const inset = {
      x: stageSize.width > 0 ? metrics.width / 2 / stageSize.width : 0,
      y: stageSize.height > 0 ? metrics.height / 2 / stageSize.height : 0,
    };
    setSelectedIndex((prev) => {
      const drag = dragRef.current;
      const anchor =
        drag?.type === "resize"
          ? { x: drag.centerX, y: drag.centerY }
          : selectionAnchorRef.current;
      if (!anchor && prev != null && markFitsOnCanvas(marks[prev], inset.x, inset.y)) {
        return prev;
      }
      const next = pickTileSelectionIndex(marks, anchor, inset);
      return next == null ? prev : next;
    });
  }, [marks, watermark?.tile]);

  useEffect(() => {
    if (!logoCropActive) return undefined;
    // Keep a mark selected so crop UI stays interactive.
    setSelectedIndex((prev) =>
      prev != null && prev < marks.length ? prev : 0
    );
    const onKey = (e) => {
      if (e.key === "Escape") {
        const wm = watermarkRef.current;
        const start = wm?.logoCropSessionStartRect;
        patchWatermark({
          logoCropActive: false,
          logoCropRect: start
            ? normalizeCropRect(start)
            : wm?.logoCropRect ?? null,
          logoCropSessionStartRect: null,
          logoCropSessionStartImageFrame: null,
          logoCropFrame: null,
          logoCropImageFrame: null,
        });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [logoCropActive, marks.length, patchWatermark]);

  useEffect(() => {
    editingIndexRef.current = editingIndex;
  }, [editingIndex]);

  useEffect(() => {
    setEditingIndex(null);
  }, [isLogoMode, watermark?.contentType]);

  // Sync canvas draft when text is edited from the left panel.
  useEffect(() => {
    if (editingIndex == null) return;
    const leftInput = document.getElementById("wmc-text");
    if (document.activeElement !== leftInput) return;
    const next = watermark?.text ?? "";
    setEditDraft(next);
    editDraftRef.current = next;
  }, [editingIndex, watermark?.text]);

  useEffect(() => {
    if (editingIndex == null) {
      editReadyRef.current = false;
      return undefined;
    }
    editReadyRef.current = false;
    const token = editFocusTokenRef.current;
    const focusEditor = () => {
      if (token !== editFocusTokenRef.current) return;
      const input = editInputRef.current;
      if (!input) return;
      input.focus({ preventScroll: true });
      const len = input.value?.length ?? 0;
      try {
        input.setSelectionRange(len, len);
      } catch {
        // ignore
      }
      // Ignore blur commits until focus has settled (double-click race).
      window.setTimeout(() => {
        if (token === editFocusTokenRef.current) {
          editReadyRef.current = true;
        }
      }, 120);
    };
    const id = window.setTimeout(focusEditor, 0);
    return () => window.clearTimeout(id);
  }, [editingIndex]);

  const effectCss = isLogoMode
    ? {}
    : watermarkEffectStyle(
        watermark?.effect || "none",
        watermark?.color || "#FFFFFF"
      );

  const selected = selectedIndex != null ? marks[selectedIndex] : null;
  selectedIndexRef.current = selectedIndex;
  const boxPad = 0;
  const selW = markMetrics.width + boxPad * 2;
  const selH = markMetrics.height + boxPad * 2;

  const editBoxMetrics = useMemo(() => {
    if (!textEditing || isLogoMode) return markMetrics;
    // Keep the same metrics as the selected mark so entering edit does not jump.
    return measureWatermarkText(
      editDraft || "Your Text",
      fontSizePx,
      fontFamily,
      watermark?.fontWeight,
      {
        letterSpacing: watermark?.letterSpacing,
        textScript: watermark?.textScript,
      }
    );
  }, [
    textEditing,
    isLogoMode,
    editDraft,
    fontSizePx,
    fontFamily,
    watermark?.fontWeight,
    watermark?.letterSpacing,
    watermark?.textScript,
    markMetrics,
  ]);

  const endDrag = useCallback(() => {
    dragRef.current = null;
  }, []);

  const commitTextEdit = useCallback(
    (textOverride) => {
      if (editingIndexRef.current == null) return;
      const nextText =
        textOverride ??
        editInputRef.current?.value ??
        editDraftRef.current ??
        "";
      editDraftRef.current = nextText;
      if (onWatermarkChange) {
        patchWatermarkRef.current({ text: nextText });
      }
      editingIndexRef.current = null;
      setEditingIndex(null);
    },
    [onWatermarkChange]
  );

  const commitTextEditRef = useRef(commitTextEdit);
  commitTextEditRef.current = commitTextEdit;

  useEffect(() => {
    if (showMarks) return;
    if (editingIndexRef.current != null) {
      commitTextEditRef.current();
    }
    dragRef.current = null;
  }, [showMarks]);

  const beginTextEditAt = useCallback(
    (index) => {
      if (!showMarksRef.current || isLogoMode || loading || !onWatermarkChange)
        return;
      endDrag();
      const next = watermarkRef.current?.text ?? "";
      editDraftRef.current = next;
      setEditDraft(next);
      editFocusTokenRef.current += 1;
      editingIndexRef.current = index;
      setEditingIndex(index);
      setSelectedIndex(index);
    },
    [endDrag, isLogoMode, loading, onWatermarkChange]
  );

  useEffect(() => {
    const onMove = (e) => {
      const drag = dragRef.current;
      if (!drag || !watermarkRef.current || !showMarksRef.current) return;

      if (drag.type === "move-pending" || drag.type === "move") {
        const dx = e.clientX - drag.startClientX;
        const dy = e.clientY - drag.startClientY;
        if (drag.type === "move-pending") {
          if (Math.hypot(dx, dy) < 4) return;
          drag.moved = true;
          drag.type = "move";
          lastTapRef.current = { time: 0, index: null };
          e.preventDefault();
        }
        const normDx = dx / stageSize.width;
        const normDy = dy / stageSize.height;
        const nextOffsetX = drag.startOffsetX + normDx;
        const nextOffsetY = drag.startOffsetY + normDy;
        const wm = watermarkRef.current || {};
        const tile = wm.tile || "single";
        if (tile !== "single") {
          selectionAnchorRef.current = {
            x: (drag.startMarkX ?? 0.5) + (nextOffsetX - drag.startOffsetX),
            y: (drag.startMarkY ?? 0.5) + (nextOffsetY - drag.startOffsetY),
          };
          patchWatermarkRef.current({
            offsetX: nextOffsetX,
            offsetY: nextOffsetY,
          });
          return;
        }
        const metrics = markMetricsRef.current;
        const base = resolveSingleMarkPosition({
          position: wm.position || "center",
          margin: wm.margin,
          stageWidth: stageSize.width,
          stageHeight: stageSize.height,
          textWidth: metrics.width,
          textHeight: metrics.height,
          rotationDeg: Number(wm.rotation) || 0,
          flushEdges: wm.flushEdges !== false,
        });
        const synced = syncMarginFromNearestEdge({
          centerX: base.x + nextOffsetX,
          centerY: base.y + nextOffsetY,
          position: wm.position || "center",
          stageWidth: stageSize.width,
          stageHeight: stageSize.height,
          textWidth: metrics.width,
          textHeight: metrics.height,
          rotationDeg: Number(wm.rotation) || 0,
          flushEdges: wm.flushEdges !== false,
        });
        patchWatermarkRef.current({
          offsetX: synced.offsetX,
          offsetY: synced.offsetY,
          margin: synced.margin,
        });
        return;
      }

      if (drag.type === "resize") {
        const rect = canvasRef.current?.getBoundingClientRect();
        if (!rect || !stageSize.width || !stageSize.height) return;

        const ax = rect.left + drag.anchorX * stageSize.width;
        const ay = rect.top + drag.anchorY * stageSize.height;
        const isEdge = drag.corner === "w" || drag.corner === "e";

        let scale;
        if (isEdge) {
          // Horizontal edge handles: scale from the opposite vertical edge.
          const dx = e.clientX - ax;
          const startDx = drag.startClientX - ax;
          if (Math.abs(startDx) < 4) return;
          scale = dx / startDx;
        } else {
          // Corner handles: drag in/out from the opposite corner.
          const dist = Math.hypot(e.clientX - ax, e.clientY - ay);
          if (drag.startAnchorDist < 4) return;
          scale = dist / drag.startAnchorDist;
        }

        if (!Number.isFinite(scale) || scale <= 0) return;

        const nextSize = Math.max(
          0.5,
          Math.min(24, drag.startSize * scale)
        );
        const wmTile = watermarkRef.current?.tile || "single";
        // Tiled marks share one size. Shifting offset here pans the pattern
        // and throws the selection box off the canvas.
        if (wmTile !== "single") {
          patchWatermarkRef.current({
            size: Math.round(nextSize * 100) / 100,
          });
          return;
        }
        const appliedScale = nextSize / drag.startSize;
        const newHalfW = drag.startHalfW * appliedScale;
        const newHalfH = drag.startHalfH * appliedScale;

        let newCenterX =
          drag.anchorX + (drag.signX || 0) * (newHalfW / stageSize.width);
        let newCenterY = isEdge
          ? drag.centerY
          : drag.anchorY + (drag.signY || 0) * (newHalfH / stageSize.height);

        const wm = watermarkRef.current || {};
        const metrics = {
          width: Math.max(1, newHalfW * 2),
          height: Math.max(1, newHalfH * 2),
        };
        const base = resolveSingleMarkPosition({
          position: wm.position || "center",
          margin: wm.margin,
          stageWidth: stageSize.width,
          stageHeight: stageSize.height,
          textWidth: metrics.width,
          textHeight: metrics.height,
          rotationDeg: Number(wm.rotation) || 0,
          flushEdges: wm.flushEdges !== false,
        });

        patchWatermarkRef.current({
          size: Math.round(nextSize * 100) / 100,
          offsetX: newCenterX - base.x,
          offsetY: newCenterY - base.y,
        });
        return;
      }

      if (drag.type === "rotate") {
        const rect = canvasRef.current?.getBoundingClientRect();
        if (!rect) return;
        const cx = rect.left + drag.centerX * stageSize.width;
        const cy = rect.top + drag.centerY * stageSize.height;
        const angle =
          (Math.atan2(e.clientY - cy, e.clientX - cx) * 180) / Math.PI;
        let next = drag.startRotation + (angle - drag.startAngle);
        // Normalize to -180..180
        next = ((next + 180) % 360 + 360) % 360 - 180;
        const wm = watermarkRef.current || {};
        const metrics = markMetricsRef.current;
        patchWatermarkRef.current(
          watermarkRotationPreservingCenter({
            nextRotation: Math.round(next),
            centerX: drag.centerX,
            centerY: drag.centerY,
            position: wm.position || "center",
            margin: wm.margin,
            stageWidth: stageSize.width,
            stageHeight: stageSize.height,
            textWidth: metrics.width,
            textHeight: metrics.height,
            flushEdges: wm.flushEdges !== false,
          })
        );
      }
    };

    const onUp = () => {
      const idx = selectedIndexRef.current;
      const mark = marksRef.current?.[idx];
      if (mark && markFitsOnCanvas(mark, 0, 0)) {
        selectionAnchorRef.current = { x: mark.x, y: mark.y };
      }
      endDrag();
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [endDrag, stageSize.width, stageSize.height]);

  const openTextEdit = useCallback(
    (e, index) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      endDrag();
      lastTapRef.current = { time: 0, index: null };
      beginTextEditAt(index);
    },
    [beginTextEditAt, endDrag]
  );

  function rememberTileSelection(index) {
    const list = marksRef.current || [];
    const tile = watermarkRef.current?.tile || "single";
    const clicked = list[index];
    const metrics = markMetricsRef.current || { width: 0, height: 0 };
    const inset = {
      x: stageSize.width > 0 ? metrics.width / 2 / stageSize.width : 0,
      y: stageSize.height > 0 ? metrics.height / 2 / stageSize.height : 0,
    };
    const next =
      tile !== "single" && !markFitsOnCanvas(clicked, inset.x, inset.y)
        ? pickTileSelectionIndex(list, clicked || null, inset)
        : index;
    const mark = list[next];
    if (mark) selectionAnchorRef.current = { x: mark.x, y: mark.y };
    return next == null ? index : next;
  }

  const startMove = (e, index) => {
    if (!showMarksRef.current || !onWatermarkChange || loading) return;

    // Logo: select + drag only (no double-tap text edit).
    // While cropping, allow re-select but do not start a move drag.
    if (isLogoMode) {
      e.preventDefault();
      e.stopPropagation();
      const picked = rememberTileSelection(index);
      const pickedMark = marksRef.current?.[picked];
      setSelectedIndex(picked);
      if (watermarkRef.current?.logoCropActive) return;
      dragRef.current = {
        type: "move-pending",
        index: picked,
        moved: false,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startOffsetX: Number(watermarkRef.current?.offsetX) || 0,
        startOffsetY: Number(watermarkRef.current?.offsetY) || 0,
        startMarkX: pickedMark?.x ?? 0.5,
        startMarkY: pickedMark?.y ?? 0.5,
      };
      return;
    }

    // Let the dedicated double-click handler own edit mode.
    if (e.detail >= 2) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (editingIndexRef.current != null) {
      commitTextEditRef.current();
    }

    const now = performance.now();
    const lastTap = lastTapRef.current;
    const isSecondTap =
      lastTap.index === index && now - lastTap.time < 450;

    // Touch / trackpad second tap → edit (desktop uses onDoubleClick).
    if (isSecondTap) {
      openTextEdit(e, index);
      return;
    }

    lastTapRef.current = { time: now, index };
    const picked = rememberTileSelection(index);
    setSelectedIndex(picked);
    const pickedMark = marksRef.current?.[picked];
    dragRef.current = {
      type: "move-pending",
      index: picked,
      moved: false,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startOffsetX: Number(watermarkRef.current?.offsetX) || 0,
      startOffsetY: Number(watermarkRef.current?.offsetY) || 0,
      startMarkX: pickedMark?.x ?? 0.5,
      startMarkY: pickedMark?.y ?? 0.5,
    };
  };

  const startResize = (e, corner) => {
    if (!showMarksRef.current || !onWatermarkChange || !selected || loading)
      return;
    if (editingIndexRef.current != null) {
      commitTextEditRef.current();
    }
    e.preventDefault();
    e.stopPropagation();
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect || !stageSize.width || !stageSize.height) return;

    const metrics = markMetricsRef.current || { width: 1, height: 1 };
    const halfW = Math.max(1, metrics.width / 2);
    const halfH = Math.max(1, metrics.height / 2);
    const halfWNorm = halfW / stageSize.width;
    const halfHNorm = halfH / stageSize.height;

    let signX = 0;
    let signY = 0;
    if (corner.includes("e")) signX = 1;
    if (corner.includes("w")) signX = -1;
    if (corner.includes("s")) signY = 1;
    if (corner.includes("n")) signY = -1;

    // Keep the opposite corner/edge fixed while this handle moves.
    const anchorX = selected.x - signX * halfWNorm;
    const anchorY =
      signY === 0 ? selected.y : selected.y - signY * halfHNorm;
    const ax = rect.left + anchorX * stageSize.width;
    const ay = rect.top + anchorY * stageSize.height;
    const startAnchorDist = Math.hypot(e.clientX - ax, e.clientY - ay);

    try {
      e.currentTarget?.setPointerCapture?.(e.pointerId);
    } catch {
      // ignore
    }

    dragRef.current = {
      type: "resize",
      corner,
      signX,
      signY,
      anchorX,
      anchorY,
      centerX: selected.x,
      centerY: selected.y,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startAnchorDist: Math.max(startAnchorDist, 8),
      startDist: Math.max(startAnchorDist, 8),
      startSize: Math.max(0.01, Number(watermarkRef.current?.size) || 1.5),
      startHalfW: halfW,
      startHalfH: halfH,
    };
  };

  const startRotate = (e) => {
    if (!showMarksRef.current || !onWatermarkChange || !selected || loading)
      return;
    if (editingIndexRef.current != null) {
      commitTextEditRef.current();
    }
    e.preventDefault();
    e.stopPropagation();
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cx = rect.left + selected.x * stageSize.width;
    const cy = rect.top + selected.y * stageSize.height;
    const startAngle =
      (Math.atan2(e.clientY - cy, e.clientX - cx) * 180) / Math.PI;
    dragRef.current = {
      type: "rotate",
      centerX: selected.x,
      centerY: selected.y,
      startAngle,
      startRotation: Number(watermarkRef.current?.rotation) || 0,
    };
  };

  return (
    <ToolPrepCanvasStage
      className={`transform-preview watermark-preview${
        imageUrl ? " has-source" : " is-empty"
      }`}
      ref={stageRef}
      metaImageUrl={imageUrl}
      metaWidth={naturalSize.width}
      metaHeight={naturalSize.height}
      onDragOver={
        onFieldDrop
          ? (e) => {
              if (
                e.dataTransfer?.types?.includes?.("application/x-sign-pdf-field") ||
                Array.from(e.dataTransfer?.types || []).includes(
                  "application/x-sign-pdf-field"
                )
              ) {
                e.preventDefault();
                e.dataTransfer.dropEffect = "copy";
              }
            }
          : undefined
      }
      onDrop={
        onFieldDrop
          ? (e) => {
              e.preventDefault();
              const kind = e.dataTransfer.getData(
                "application/x-sign-pdf-field"
              );
              if (kind) onFieldDrop(kind, e);
            }
          : undefined
      }
    >
      <div
        ref={canvasRef}
        className={`transform-preview__canvas${
          checker ? " transform-preview__canvas--checker" : ""
        }`}
        style={{
          width: stageSize.width,
          height: stageSize.height,
          backgroundColor: checker ? undefined : canvasBg,
        }}
        onPointerDown={(e) => {
          if (editingIndexRef.current != null) {
            commitTextEditRef.current();
          }
          // Keep selection while cropping so Done/Cancel stay available.
          if (watermarkRef.current?.logoCropActive) return;
          if (e.target === e.currentTarget) setSelectedIndex(null);
        }}
      >
        {loading ? (
          <div className="transform-preview__loading">
            <RoundLoader size="md" label={loadingLabel} />
          </div>
        ) : imageUrl ? (
          <>
            <img
              src={imageUrl}
              alt="Watermark preview"
              className={
                transform.width > 0
                  ? "transform-preview__image"
                  : "transform-preview__image transform-preview__image--contain"
              }
              style={
                transform.width > 0
                  ? {
                      width: transform.width,
                      height: transform.height,
                      left: transform.x,
                      top: transform.y,
                    }
                  : undefined
              }
              draggable={false}
              onPointerDown={() => {
                if (editingIndexRef.current != null) {
                  commitTextEditRef.current();
                }
                if (watermarkRef.current?.logoCropActive) return;
                setSelectedIndex(null);
              }}
            />
            <div className="watermark-preview__overlay">
              <div className="watermark-preview__marks-clip">
              {showMarks && ghostWatermark ? (
                <WatermarkThumbMarks
                  watermark={ghostWatermark}
                  width={stageSize.width}
                  height={stageSize.height}
                />
              ) : null}
              {showMarks && Array.isArray(extraWatermarks)
                ? extraWatermarks.map((extra, idx) =>
                    extra ? (
                      <WatermarkThumbMarks
                        key={
                          extra.signPdfPlacementId || `extra-wm-${idx}`
                        }
                        watermark={extra}
                        width={stageSize.width}
                        height={stageSize.height}
                        selectable={Boolean(onSelectExtraWatermark)}
                        onSelect={onSelectExtraWatermark}
                      />
                    ) : null
                  )
                : null}
              {showMarks
                ? marks.map((pos, i) => {
                const isSelected = selectedIndex === i;
                const isEditingThis = editingIndex === i && !isLogoMode;
                const markKey = `wm-mark-${i}`;
                const editorMetrics = isEditingThis
                  ? measureWatermarkText(editDraft || "Your Text", fontSizePx, fontFamily, watermark?.fontWeight
                    , {
                      letterSpacing: watermark?.letterSpacing,
                      textScript: watermark?.textScript,
                    })
                  : markMetrics;
                const sharedStyle = {
                  left: `${pos.x * 100}%`,
                  top: `${pos.y * 100}%`,
                  opacity:
                    Math.max(
                      0,
                      Math.min(100, Number(watermark?.opacity) || 0)
                    ) / 100,
                  transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
                  zIndex: isSelected ? 5 : 2,
                };
                const displayText = watermark?.text?.trim()
                  ? watermark.text
                  : "Your Text";
                const textAlign = watermark?.textAlign || "center";
                const scriptMode = watermark?.textScript || "normal";
                const textStyle = {
                  color: watermark?.color || "#FFFFFF",
                  fontFamily,
                  fontSize: fontSizePx,
                  fontWeight: watermark?.fontWeight === "bold" ? 700 : 400,
                  fontStyle: watermarkFontCssStyle(watermark?.fontStyle),
                  textDecoration: watermarkTextDecoration(watermark),
                  ...effectCss,
                  ...watermarkTextAdvancedCss(watermark, fontSizePx),
                  ...watermarkTextBoxLayout(textAlign, editorMetrics, {
                    // Keep box edge-to-edge with glyphs (no extra side pad when selected).
                    boxWidth: undefined,
                    extraWidth: 0,
                    textScript: scriptMode,
                  }),
                };
                const markText = (
                  <WatermarkMarkText text={displayText} script={scriptMode} />
                );
                if (isLogoMode) {
                  const baseTransform = `translate(-50%, -50%) rotate(${rotation}deg)`;
                  const { style: logoStyle, tint, imgStyle, effect, effectColor } =
                    watermarkLogoMarkStyle(watermark, baseTransform);
                  const showCropUi = logoCropActive && isSelected;
                  return (
                    <span
                      key={markKey}
                      className={`watermark-preview__mark watermark-preview__mark--logo${
                        isSelected ? " is-selected" : ""
                      }${showCropUi ? " is-cropping" : ""}`}
                      role="button"
                      tabIndex={0}
                      aria-label={
                        showCropUi
                          ? "Crop logo watermark"
                          : isSelected
                            ? "Selected logo watermark — drag to move"
                            : "Select logo watermark"
                      }
                      aria-pressed={isSelected}
                      onPointerDown={(e) => startMove(e, i)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setSelectedIndex(i);
                        }
                      }}
                      style={{
                        ...sharedStyle,
                        ...logoStyle,
                        width: markMetrics.width,
                        height: markMetrics.height,
                        overflow: showCropUi ? "visible" : undefined,
                      }}
                    >
                      {showCropUi ? (
                        <WatermarkLogoCropOverlay
                          logoUrl={logoUrl}
                          cropFrame={
                            cropHostFrames?.cropFrame ||
                            logoCropRect ||
                            DEFAULT_CROP_RECT
                          }
                          imageFrame={
                            cropHostFrames?.imageFrame || DEFAULT_IMAGE_FRAME
                          }
                          cropAspect={watermark?.logoCropAspect || "custom"}
                          naturalWidth={watermark?.logoNaturalWidth}
                          naturalHeight={watermark?.logoNaturalHeight}
                          width={markMetrics.width}
                          height={markMetrics.height}
                          tint={tint}
                          effect={effect}
                          effectColor={effectColor}
                          onFramesChange={(nextCrop, nextImage) =>
                            patchWatermark({
                              logoCropFrame: nextCrop,
                              logoCropImageFrame: nextImage,
                              logoCropRect: hostFramesToSourceRect(
                                nextCrop,
                                nextImage
                              ),
                            })
                          }
                          onDone={() =>
                            patchWatermark({
                              logoCropActive: false,
                              logoCropSessionStartRect: null,
                              logoCropSessionStartImageFrame: null,
                            })
                          }
                          onCancel={() => {
                            const wm = watermarkRef.current;
                            const start = wm?.logoCropSessionStartRect;
                            patchWatermark({
                              logoCropActive: false,
                              logoCropRect: start
                                ? normalizeCropRect(start)
                                : logoCropRect,
                              logoCropSessionStartRect: null,
                              logoCropSessionStartImageFrame: null,
                            });
                          }}
                        />
                      ) : (
                        <WatermarkLogoMarkMedia
                          logoUrl={logoUrl}
                          tint={tint}
                          effect={effect}
                          effectColor={effectColor}
                          imgStyle={imgStyle}
                        />
                      )}
                    </span>
                  );
                }
                if (isEditingThis) {
                  const editW = Math.max(1, editorMetrics.width);
                  const editH = Math.max(1, editorMetrics.height);
                  return (
                    <input
                      key={markKey}
                      ref={editInputRef}
                      type="text"
                      className="watermark-preview__mark-editor"
                      value={editDraft}
                      placeholder="Your Text"
                      spellCheck={false}
                      autoComplete="off"
                      aria-label="Edit watermark text"
                      onChange={(e) => {
                        editDraftRef.current = e.target.value;
                        setEditDraft(e.target.value);
                      }}
                      onBlur={() => {
                        if (!editReadyRef.current) {
                          // Double-click can steal focus briefly — put it back.
                          window.setTimeout(() => {
                            editInputRef.current?.focus({ preventScroll: true });
                          }, 0);
                          return;
                        }
                        window.setTimeout(() => {
                          if (editInputRef.current === document.activeElement) {
                            return;
                          }
                          commitTextEditRef.current();
                        }, 0);
                      }}
                      onPointerDown={(e) => e.stopPropagation()}
                      onDoubleClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === "Escape") {
                          e.preventDefault();
                          commitTextEditRef.current();
                        }
                        if (e.key === "Enter") {
                          e.preventDefault();
                          commitTextEditRef.current();
                        }
                      }}
                      style={{
                        ...sharedStyle,
                        color: watermark?.color || "#FFFFFF",
                        fontFamily,
                        fontSize: fontSizePx,
                        fontWeight: watermark?.fontWeight === "bold" ? 700 : 400,
                        fontStyle: watermarkFontCssStyle(watermark?.fontStyle),
                        textDecoration: watermarkTextDecoration(watermark),
                        letterSpacing: textStyle.letterSpacing,
                        ...effectCss,
                        width: editW,
                        height: editH,
                        minWidth: editW,
                        minHeight: editH,
                        padding: 0,
                        margin: 0,
                        background: "transparent",
                        border: "none",
                        borderRadius: 0,
                        boxSizing: "content-box",
                        display: "block",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        lineHeight: `${editH}px`,
                        textAlign: "left",
                        outline: "none",
                        boxShadow: "none",
                        caretColor: "#ffffff",
                        zIndex: 9,
                      }}
                    />
                  );
                }
                return (
                  <span
                    key={markKey}
                    className={`watermark-preview__mark${
                      isSelected ? " is-selected" : ""
                    }`}
                    role="button"
                    tabIndex={0}
                    aria-label={
                      isSelected
                        ? "Selected watermark — double-click to edit, drag to move"
                        : "Select watermark — double-click to edit"
                    }
                    aria-pressed={isSelected}
                    onPointerDown={(e) => startMove(e, i)}
                    onDoubleClick={(e) => openTextEdit(e, i)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (isSelected && editingIndex == null) {
                          openTextEdit(null, i);
                        } else {
                          setSelectedIndex(i);
                        }
                      }
                      if (e.key === " ") {
                        e.preventDefault();
                        setSelectedIndex(i);
                      }
                    }}
                    style={{
                      ...sharedStyle,
                      ...textStyle,
                    }}
                  >
                    {markText}
                  </span>
                );
              })
              : null}
              </div>

              {showMarks && selected && hasContent && !textEditing && !logoCropActive ? (
                <div
                  className="watermark-preview__selection"
                  style={{
                    left: `${selected.x * 100}%`,
                    top: `${selected.y * 100}%`,
                    width: selW,
                    height: selH,
                    transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
                  }}
                >
                  {HANDLES.map((h) => (
                    <span
                      key={h}
                      className={`watermark-preview__handle watermark-preview__handle--${h}`}
                      onPointerDown={(e) => startResize(e, h)}
                    />
                  ))}
                  <button
                    type="button"
                    className="watermark-preview__rotate"
                    aria-label="Rotate watermark"
                    title="Rotate"
                    onPointerDown={startRotate}
                  >
                    <svg
                      className="watermark-preview__rotate-icon"
                      viewBox="0 0 16 16"
                      aria-hidden="true"
                    >
                      <path
                        d="M13.5 8A5.5 5.5 0 1 1 8 2.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                      <path
                        d="M8 1v3.2L10.6 2.6 8 1Z"
                        fill="currentColor"
                      />
                    </svg>
                  </button>
                </div>
              ) : null}

              {showMarks && textEditing && selected && hasContent && !isLogoMode ? (
                <div
                  className="watermark-preview__selection watermark-preview__selection--editing"
                  aria-hidden="true"
                  style={{
                    left: `${selected.x * 100}%`,
                    top: `${selected.y * 100}%`,
                    width: editBoxMetrics.width,
                    height: editBoxMetrics.height,
                    transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
                  }}
                />
              ) : null}
            </div>
          </>
        ) : (
          <div className="transform-preview__empty">{emptyLabel}</div>
        )}
      </div>
    </ToolPrepCanvasStage>
  );
};

export default WatermarkPreview;
